import React, { useState, useEffect } from "react";
import { api } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

const TaxCalculator = () => {
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [netIncome, setNetIncome] = useState("");
  const [taxResult, setTaxResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchStates();
  }, []);

  const fetchStates = async () => {
    try {
      const response = await api.get("/tax-rates");
      setStates(response.data);
    } catch (error) {
      console.error("Failed to fetch tax rates:", error);
    }
  };

  const calculateTax = async () => {
    if (!selectedState || !netIncome) {
      toast.error("Please select a state and enter your net income");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post("/tax-calculator", {
        state: selectedState,
        net_income: parseFloat(netIncome),
      });
      setTaxResult(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to calculate taxes");
    } finally {
      setLoading(false);
    }
  };

  const noIncomeTaxStates = states.filter(s => s.state_income_tax === 0).map(s => s.name);

  return (
    <div className="space-y-8" data-testid="tax-calculator-page">
      <div>
        <h1 className="text-3xl font-bold text-stone-900">Self-Employment Tax Calculator</h1>
        <p className="text-stone-600">Calculate estimated taxes for all 50 states + DC</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Calculator */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Calculate Your Taxes</CardTitle>
            <CardDescription>
              Enter your self-employment net income to estimate your tax liability
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Select State</Label>
                <Select value={selectedState} onValueChange={setSelectedState}>
                  <SelectTrigger className="h-12" data-testid="state-select">
                    <SelectValue placeholder="Choose your state" />
                  </SelectTrigger>
                  <SelectContent className="max-h-80">
                    {states.map(state => (
                      <SelectItem key={state.code} value={state.code}>
                        {state.name} ({state.state_income_tax}%)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Net Self-Employment Income</Label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-500">$</span>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={netIncome}
                    onChange={(e) => setNetIncome(e.target.value)}
                    className="pl-8 h-12 text-lg"
                    placeholder="100000.00"
                    data-testid="income-input"
                  />
                </div>
                <p className="text-xs text-stone-500">After business expenses</p>
              </div>
            </div>

            <Button
              onClick={calculateTax}
              disabled={loading || !selectedState || !netIncome}
              className="w-full bg-[#1e3a5f] hover:bg-[#2d5a8e] h-12"
              data-testid="calculate-tax-btn"
            >
              {loading ? "Calculating..." : "Calculate Estimated Taxes"}
            </Button>

            {/* Results */}
            {taxResult && (
              <div className="space-y-6 animate-fade-in">
                <div className="p-6 rounded-xl bg-stone-50 border border-stone-200">
                  <h3 className="font-semibold text-stone-900 mb-4">
                    Tax Estimate for {taxResult.state_name}
                  </h3>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="p-4 rounded-lg bg-white border border-stone-100">
                      <p className="text-xs text-stone-500">Self-Employment Tax</p>
                      <p className="text-xl font-bold text-stone-900" data-testid="se-tax">
                        ${taxResult.se_tax.toLocaleString()}
                      </p>
                      <p className="text-xs text-stone-400">15.3% of 92.35%</p>
                    </div>
                    <div className="p-4 rounded-lg bg-white border border-stone-100">
                      <p className="text-xs text-stone-500">State Income Tax</p>
                      <p className="text-xl font-bold text-stone-900" data-testid="state-tax">
                        ${taxResult.state_tax.toLocaleString()}
                      </p>
                      <p className="text-xs text-stone-400">
                        {states.find(s => s.code === selectedState)?.state_income_tax || 0}%
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-white border border-stone-100">
                      <p className="text-xs text-stone-500">Federal (Est. 22%)</p>
                      <p className="text-xl font-bold text-stone-900" data-testid="federal-tax">
                        ${taxResult.federal_tax_estimate.toLocaleString()}
                      </p>
                      <p className="text-xs text-stone-400">Simplified estimate</p>
                    </div>
                    <div className="p-4 rounded-lg bg-[#1e3a5f]/5 border border-[#1e3a5f]/20">
                      <p className="text-xs text-[#1e3a5f]">SE Tax Deduction</p>
                      <p className="text-xl font-bold text-[#1e3a5f]" data-testid="se-deduction">
                        -${taxResult.se_deduction.toLocaleString()}
                      </p>
                      <p className="text-xs text-[#1e3a5f]">50% of SE tax</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-6 rounded-xl bg-amber-50 border border-amber-200">
                      <p className="text-sm text-amber-700">Total Estimated Tax</p>
                      <p className="text-4xl font-bold text-amber-800" data-testid="total-tax">
                        ${taxResult.total_tax.toLocaleString()}
                      </p>
                      <p className="text-sm text-amber-600 mt-1">
                        Effective rate: {taxResult.effective_rate}%
                      </p>
                    </div>
                    <div className="p-6 rounded-xl bg-blue-50 border border-blue-200">
                      <p className="text-sm text-blue-700">Quarterly Payment</p>
                      <p className="text-4xl font-bold text-blue-800" data-testid="quarterly-payment">
                        ${taxResult.quarterly_estimate.toLocaleString()}
                      </p>
                      <p className="text-sm text-blue-600 mt-1">
                        Due dates: Apr 15, Jun 15, Sep 15, Jan 15
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-stone-100 text-sm text-stone-600">
                  <strong>Disclaimer:</strong> This is a simplified estimate for planning purposes only. 
                  Actual tax liability may vary based on deductions, credits, filing status, and other factors. 
                  Consult a tax professional for accurate tax advice.
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Cards */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Self-Employment Tax</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-stone-600">
              <p>
                Self-employment tax is <strong>15.3%</strong> of your net earnings:
              </p>
              <ul className="space-y-2 list-disc list-inside">
                <li><strong>12.4%</strong> for Social Security (up to $168,600 in 2024)</li>
                <li><strong>2.9%</strong> for Medicare (no income limit)</li>
              </ul>
              <p className="pt-2">
                The tax is calculated on <strong>92.35%</strong> of your net self-employment income.
              </p>
              <p>
                You can deduct <strong>half</strong> of your SE tax from your gross income.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">No Income Tax States</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {noIncomeTaxStates.map(state => (
                  <span key={state} className="px-3 py-1 rounded-full bg-[#1e3a5f]/10 text-[#1e3a5f] text-xs font-medium">
                    {state}
                  </span>
                ))}
              </div>
              <p className="text-xs text-stone-500 mt-3">
                These states have no state income tax on wages/self-employment income.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Quarterly Due Dates</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {[
                  { q: "Q1", date: "April 15", period: "Jan 1 - Mar 31" },
                  { q: "Q2", date: "June 15", period: "Apr 1 - May 31" },
                  { q: "Q3", date: "September 15", period: "Jun 1 - Aug 31" },
                  { q: "Q4", date: "January 15", period: "Sep 1 - Dec 31" },
                ].map(item => (
                  <div key={item.q} className="flex justify-between items-center p-2 rounded-lg bg-stone-50">
                    <div>
                      <span className="font-medium text-stone-900">{item.q}</span>
                      <span className="text-xs text-stone-500 ml-2">{item.period}</span>
                    </div>
                    <span className="text-sm font-medium text-[#1e3a5f]">{item.date}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* State Tax Rates Table */}
      <Card>
        <CardHeader>
          <CardTitle>All State Tax Rates</CardTitle>
          <CardDescription>Reference table of state income tax rates for self-employed individuals</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border overflow-hidden max-h-96 overflow-y-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-stone-50">
                <TableRow>
                  <TableHead>State</TableHead>
                  <TableHead className="text-right">State Income Tax</TableHead>
                  <TableHead className="text-right">SE Tax</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {states.map(state => (
                  <TableRow key={state.code} className={state.state_income_tax === 0 ? "bg-[#1e3a5f]/5" : ""}>
                    <TableCell className="font-medium">{state.name}</TableCell>
                    <TableCell className="text-right">
                      {state.state_income_tax === 0 ? (
                        <span className="text-[#1e3a5f] font-medium">None</span>
                      ) : (
                        `${state.state_income_tax}%`
                      )}
                    </TableCell>
                    <TableCell className="text-right">{state.se_tax}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TaxCalculator;